import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { FeUtil, API, TYPE } from '../../../hezky-routes';
import { GenericConfirmationDialogComponent } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class TripsService {

  constructor(private readonly feUtil: FeUtil) { }

  public getTrips(params?: any): Observable<any[]> {
    return this.feUtil.httpSvc.get(API.TRIPS.GET_TRIPS, params);
  }

  public getTripById(tripId: number | string): Observable<any> {
    return this.feUtil.httpSvc.get(API.TRIPS.GET_TRIP_BY_ID(tripId));
  }

  public getTripReadings(tripId: number | string, params?: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.TRIPS.GET_TRIP_READINGS(tripId), { ...params });
  }

  public fetchTripHistoryByLegId(params?: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.TRIPS.GET_TRIP_HISTORY_BY_LEG_ID, { ...params });
  }

  public addTrip(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(API.TRIPS.ADD_TRIP, { ...payload, user_id: this.feUtil.user_data.user_id, customer_id: this.feUtil.user_data.customer.customer_id });
  }

  public patchTrip(tripId: number | string, payload: any): Observable<any> {
    return this.feUtil.httpSvc.patch(API.TRIPS.PATCH_TRIP(tripId), { ...payload, user_id: this.feUtil.user_data.user_id, customer_id: this.feUtil.user_data.customer.customer_id });
  }

  public getTripCoordinates(legId: number, params?: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.TRIPS.COORDINATES(legId), { ...params });
  }

  public uploadChecklist(id: number, payload: any): Observable<any> {
    return this.feUtil.httpSvc.patchFile(API.TRIPS.PATCH_TRIP_CHECKLIST(id), payload);
  }

  // TRIP SERVICE HELPER METHODS
  public getMatrixData(tripGraphData: any): { matrix: string, readingData: any[] }[] {
    const result: { [key: string]: any[] } = {};
    for (const sensorId in tripGraphData) {
      const sensor = tripGraphData[sensorId];
      const { datesArray, statusArray, ...dataArrays } = sensor;
      Object.entries(dataArrays).forEach(([key, dataArray]: [string, any]) => {
        if (key === 'statusArray') return;
        if (Array.isArray(dataArray) && Array.isArray(datesArray)) {
          const matrixType = key.replace('Array', '');
          const readingData = dataArray
            .map((value: any, index: number) => {
              const datetime = datesArray[index];
              let statusInfo: string = 'normal';
              if (Array.isArray(statusArray) && statusArray.length > index) {
                const v = statusArray[index];
                statusInfo = Array.isArray(v) ? (v[0] || 'normal') : (typeof v === 'string' && v.length ? v : 'normal');
              }
              let status = statusInfo === 'normal' ? statusInfo : ((typeof statusInfo === 'string' && statusInfo.toLowerCase().includes(matrixType)) ? (statusInfo.split('Incident')[0].trim() !== '' ? statusInfo.split(' ')[0].trim() : statusInfo) : 'normal')
              return {
                value: value != null ? (typeof value === 'number' ? value.toFixed(2) : value) : null,
                datetime,
                sensorId,
                status
              };
            })
            .filter((reading: any) => reading.value !== null);
          const matrixObj = {
            sensorId,
            metaData: { matrixType },
            timeseriesData: readingData,
          };
          result[matrixType] = (result[matrixType] || []).concat(matrixObj);
        }
      });
    }
    return Object.entries(result).map(([matrix, readingData]) => ({ matrix, readingData }));
  }

  public getLastLocation(trip: any, filter: 'location_id' | 'display_name' = 'display_name'): string {
    let lastLocation = '-';
    const sequence = trip?.sequence;
    let legs = trip?.legs;
    if (sequence?.length && legs?.length) {
      legs = [...legs].sort((a, b) =>
        sequence.indexOf(a.leg_id) - sequence.indexOf(b.leg_id)
      );
    }
    lastLocation = legs[legs?.length - 1]?.location?.[filter];
    return lastLocation;
  }

  public getSequencedLegs(trip: any, filter: string[] = []): any[] {
    if (!trip?.sequence || !trip?.legs) {
      console.log('No sequence or legs found');
      return [];
    }
    let sequencedLegs = [...trip.legs].sort((a: any, b: any) => trip.sequence.indexOf(a.leg_id) - trip.sequence.indexOf(b.leg_id));
    if (filter?.length) sequencedLegs = sequencedLegs.filter((leg: any) => filter.includes(leg.status));
    return sequencedLegs;
  }

  public getLegStatus(leg: any, activeLeg: any): string {
    const statusMap: { [key: string]: string } = {
      active: 'Upcoming',
      paused: 'Paused',
      discard: 'Discarded',
      completed: 'Completed'
    };
    if (activeLeg?.leg_id === leg?.leg_id && activeLeg?.status === 'paused') {
      return 'Movement in progress';
    }
    if (activeLeg?.leg_id === leg?.leg_id && activeLeg?.status === 'active') {
      return 'Next destination';
    }
    return statusMap[leg?.status];
  }

  public deleteTrip(id: number | string): Observable<boolean> {
    return this.discardTrip(id);
  }

  private discardTrip(id: number | string): Observable<boolean> {
    return new Observable((observer) => {
      const discardTrip = (password: string, remarks: string) => {
        this.patchTrip(id, { status: 'discard', password, remark: remarks || null })
          .subscribe({
            next: () => observer.next(true),
          })
      }
      this.feUtil.modalSvc.create<GenericConfirmationDialogComponent, TYPE.GenericConfirmationDialog>({
        nzTitle: 'Please confirm your password and add remarks',
        nzContent: GenericConfirmationDialogComponent,
        nzData: { displayInput: ['password', 'remarks'] },
        nzCentered: true,
        nzMaskClosable: false,
        nzClosable: false,
        nzCloseOnNavigation: true,
      }).afterClose.pipe().subscribe({
        next: (result) => {
          if (!result) {
            observer.next(false);
            return;
          }
          const { password, remarks } = result;
          if (!password) return observer.next(false);
          discardTrip(password, remarks);
        }
      })
    })
  };

}